import React, {useEffect} from 'react';

/**
 * 轨道悬停变速，移植自设计稿 js/main.js：
 * - mouseenter → playbackRate 指数缓动逼近 0（系数 0.07），误差 <0.003 收敛停帧
 * - mouseleave → 逼近 1 恢复
 * - 只控制 animationName 以 `orbit` 开头的动画（orbit / orbit-reverse / orbit-label-* / orbit-depth*）
 *   —— custom.css 中轨道动画名必须保留 orbit 前缀，改名即失控。
 */
const useOrbitSpeedControl = <T extends HTMLElement>(ref: React.RefObject<T>) => {
  useEffect(() => {
    const stage = ref.current;
    if (!stage) return undefined;

    // getAnimations() 返回 Animation[]，其中 CSS 动画实为 CSSAnimation（animationName 必为 string）
    const isOrbitAnimation = (anim: Animation): anim is CSSAnimation =>
      /^orbit/.test((anim as CSSAnimation).animationName);

    const orbitAnimations = () => stage.getAnimations({subtree: true}).filter(isOrbitAnimation);

    let targetRate = 1;
    let currentRate = 1;
    let rafId: number | null = null;

    const tick = () => {
      currentRate += (targetRate - currentRate) * 0.07;
      if (Math.abs(targetRate - currentRate) < 0.003) {
        currentRate = targetRate;
      }
      orbitAnimations().forEach((anim) => {
        anim.playbackRate = currentRate;
      });
      rafId = currentRate === targetRate ? null : window.requestAnimationFrame(tick);
    };

    const setRate = (rate: number) => {
      targetRate = rate;
      if (rafId === null) rafId = window.requestAnimationFrame(tick);
    };

    const enter = () => setRate(0);
    const leave = () => setRate(1);

    stage.addEventListener('mouseenter', enter);
    stage.addEventListener('mouseleave', leave);

    return () => {
      stage.removeEventListener('mouseenter', enter);
      stage.removeEventListener('mouseleave', leave);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      // 离开首页后把轨道动画复位，避免 Web Animations 缓存住非 1 的速率
      orbitAnimations().forEach((anim) => {
        anim.playbackRate = 1;
      });
    };
  }, [ref]);
};

export default useOrbitSpeedControl;
